import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, Star, Calendar, FileText, Car, Gift, 
  Clock, CheckCircle, XCircle, AlertCircle, Receipt, Download, Euro, Printer, MessageSquare, Mail, Truck, MapPin, Phone, Check, X,
  History, ClipboardList, Wallet, Wrench, Droplets, CreditCard, Banknote, ListChecks, Edit2, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500',
  pending_payment: 'bg-orange-500',
  confirmed: 'bg-blue-500',
  in_progress: 'bg-purple-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
  sent: 'bg-blue-500',
  paid: 'bg-green-500'
};

const statusLabels = {
  pending: 'En attente',
  pending_payment: 'Paiement en attente',
  confirmed: 'Confirmé',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
  sent: 'À payer',
  paid: 'Payée'
};

// Function to download invoice as PDF
const downloadInvoice = (invoice) => {
  const printWindow = window.open('', '_blank');
  const logoUrl = 'https://customer-assets.emergentagent.com/job_atelier-mecanique/artifacts/2w27kxnb_IMG_0321.jpeg';
  const invoiceHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Facture ${invoice.invoice_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; background: #fff; }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
        .logo-section { }
        .logo-img { height: 80px; }
        .invoice-info { text-align: right; }
        .invoice-number { font-size: 22px; font-weight: bold; color: #dc2626; }
        .invoice-date { color: #666; margin-top: 5px; font-size: 13px; }
        .addresses { display: flex; justify-content: space-between; margin-bottom: 30px; margin-top: 30px; }
        .address-block { width: 45%; }
        .address-block h3 { color: #dc2626; margin-bottom: 10px; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
        .address-block p { line-height: 1.6; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
        th { background: #f3f4f6; padding: 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #dc2626; font-size: 12px; }
        td { padding: 12px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
        .text-right { text-align: right; }
        .totals { width: 300px; margin-left: auto; }
        .totals tr td { padding: 8px 12px; }
        .totals .total-row { font-weight: bold; font-size: 16px; background: #dc2626; color: white; }
        .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #666; font-size: 10px; }
        .status { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
        .status-paid { background: #22c55e; color: white; }
        .status-sent { background: #3b82f6; color: white; }
        .notes { background: #f9fafb; padding: 15px; border-radius: 8px; margin-top: 20px; font-style: italic; color: #666; font-size: 12px; }
        .bank-info { background: #f0f0f0; padding: 15px; border-radius: 8px; margin-top: 20px; font-size: 11px; }
        .bank-info strong { color: #dc2626; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo-section">
          <img src="${logoUrl}" alt="Le Hub Atelier Auto Moto" class="logo-img" />
        </div>
        <div class="invoice-info">
          <div class="invoice-number">${invoice.invoice_number}</div>
          <div class="invoice-date">Date: ${new Date(invoice.created_at).toLocaleDateString('fr-FR')}</div>
          ${invoice.due_date ? `<div class="invoice-date">Échéance: ${new Date(invoice.due_date).toLocaleDateString('fr-FR')}</div>` : ''}
          <div style="margin-top: 10px;">
            <span class="status status-${invoice.status}">${invoice.status === 'paid' ? 'PAYÉE' : 'À PAYER'}</span>
          </div>
        </div>
      </div>

      <div class="addresses">
        <div class="address-block">
          <h3>Émetteur</h3>
          <p>
            <strong>KD AUTO SERVICE - SAS</strong><br>
            Le Hub Atelier Auto Moto<br>
            SIRET: 989 707 260 00018<br>
            TVA Intracom.: FR01 989 707 260<br>
            Tél: 0805.032.032<br>
            info@lehubatelier.fr<br>
            www.lehubatelier.fr
          </p>
        </div>
        <div class="address-block">
          <h3>Client</h3>
          <p>
            ${invoice.user_company ? `<strong>${invoice.user_company}</strong><br>` : ''}
            <strong>${invoice.user_firstname ? invoice.user_firstname + ' ' : ''}${invoice.user_name}</strong><br>
            ${invoice.user_address ? invoice.user_address.replace(/\n/g, '<br>') + '<br>' : ''}
            ${invoice.user_phone ? `Tél: ${invoice.user_phone}<br>` : ''}
            ${invoice.user_email}
          </p>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-right">Quantité</th>
            <th class="text-right">Prix unitaire HT</th>
            <th class="text-right">Total HT</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-right">${item.quantity}</td>
              <td class="text-right">${item.unit_price.toFixed(2)} €</td>
              <td class="text-right">${item.total.toFixed(2)} €</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <table class="totals">
        <tr>
          <td>Sous-total HT</td>
          <td class="text-right">${invoice.subtotal.toFixed(2)} €</td>
        </tr>
        <tr>
          <td>TVA (${invoice.tax_rate}%)</td>
          <td class="text-right">${invoice.tax_amount.toFixed(2)} €</td>
        </tr>
        <tr class="total-row">
          <td>Total TTC</td>
          <td class="text-right">${invoice.total.toFixed(2)} €</td>
        </tr>
      </table>

      ${invoice.notes ? `<div class="notes"><strong>Notes:</strong> ${invoice.notes}</div>` : ''}

      <div class="bank-info">
        <strong>Coordonnées bancaires pour règlement :</strong><br>
        RIB: FR76 1010 7001 1100 31808375 451
      </div>

      ${invoice.status === 'paid' && invoice.paid_date ? `
        <p style="margin-top: 20px; color: #22c55e; font-weight: bold;">
          ✓ Facture payée le ${new Date(invoice.paid_date).toLocaleDateString('fr-FR')}
        </p>
      ` : ''}

      <div class="footer">
        <p><strong>KD AUTO SERVICE - SAS</strong> - SIRET: 989 707 260 00018 - TVA Intracommunautaire: FR01 989 707 260</p>
        <p style="margin-top: 5px;">www.lehubatelier.fr - Tél: 0805.032.032 - info@lehubatelier.fr</p>
        <p style="margin-top: 10px;">Merci pour votre confiance !</p>
      </div>

      <script>
        window.onload = function() { window.print(); }
      </script>
    </body>
    </html>
  `;
  printWindow.document.write(invoiceHTML);
  printWindow.document.close();
};

export default function AccountPage() {
  const { user, token, loading: authLoading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [towingRequests, setTowingRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // New states for history
  const [history, setHistory] = useState(null);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [tracking, setTracking] = useState([]);
  const [paymentsData, setPaymentsData] = useState({ payments: [], summary: { total_paid: 0, total_pending: 0, total_failed: 0, count: 0 } });
  const [adminQuotes, setAdminQuotes] = useState([]);
  const [alerts, setAlerts] = useState({ quotes: [], invoices: [], unread_count: 0 });
  // Read tab from URL query (e.g. ?tab=quotes) to allow deep-links from email/badge
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('tab') || 'history';
    } catch { return 'history'; }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/user/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(res.data || { quotes: [], invoices: [], unread_count: 0 });
    } catch (_) { /* silent */ }
  };

  // Open a quote/invoice PDF in a new tab and mark it as viewed
  const openDocumentPdf = async (kind, id) => {
    if (!id) return;
    const path = kind === 'quote' ? `user/quotes/${id}/pdf` : `user/invoices/${id}/pdf`;
    try {
      const res = await axios.get(`${API}/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // Mark as viewed (server already marks during /pdf, but call to be safe and refresh)
      const markPath = kind === 'quote'
        ? `user/quotes/${id}/mark-viewed`
        : `user/invoices/${id}/mark-viewed`;
      await axios.post(`${API}/${markPath}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
      fetchAlerts();
    } catch (e) {
      toast.error('Impossible d\u2019ouvrir le PDF');
    }
  };

  const dismissAlert = async (kind, id) => {
    const markPath = kind === 'quote'
      ? `user/quotes/${id}/mark-viewed`
      : `user/invoices/${id}/mark-viewed`;
    try {
      await axios.post(`${API}/${markPath}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAlerts();
    } catch (_) { /* silent */ }
  };

  // Modification de RDV mecanique
  const [editingAppt, setEditingAppt] = useState(null);
  const [apptForm, setApptForm] = useState({});
  const [apptSaving, setApptSaving] = useState(false);

  // Verifie si modification possible (3h avant le RDV)
  const canEditAppt = (apt) => {
    if (!apt || apt.status === 'cancelled' || apt.status === 'completed') return false;
    try {
      const dt = new Date(`${apt.date}T${apt.time_slot}:00`);
      const diffH = (dt.getTime() - Date.now()) / 3600000;
      return diffH >= 3;
    } catch { return false; }
  };

  const openEditAppt = (apt) => {
    setEditingAppt(apt);
    setApptForm({
      date: apt.date || '',
      time_slot: apt.time_slot || '',
      vehicle_brand: apt.vehicle_brand || '',
      vehicle_model: apt.vehicle_model || '',
      vehicle_plate: apt.vehicle_plate || '',
      notes: apt.notes || ''
    });
  };

  const saveAppt = async () => {
    if (!editingAppt) return;
    setApptSaving(true);
    try {
      await axios.patch(`${API}/appointments/${editingAppt.id}`, apptForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rendez-vous modifie');
      setEditingAppt(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setApptSaving(false);
    }
  };

  const cancelAppt = async (apt) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/appointments/${apt.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rendez-vous annule');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/connexion');
      return;
    }
    // Compte comptable : redirige vers le portail comptabilite
    if (user.role === 'accountant') {
      navigate('/comptabilite');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [apptRes, repRes, subRes, invRes, msgRes, towingRes, quotesRes, historyRes, trackingRes, paymentsRes, adminQuotesRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/repairs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/subscriptions/my`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/invoices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/messages/inbox`, { headers }).catch(() => ({ data: { messages: [], unread_count: 0 } })),
        axios.get(`${API}/towing/my-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/quotes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/history`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/user/tracking`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/payments`, { headers }).catch(() => ({ data: { payments: [], summary: { total_paid: 0, total_pending: 0, total_failed: 0, count: 0 } } })),
        axios.get(`${API}/user/admin-quotes`, { headers }).catch(() => ({ data: [] }))
      ]);
      setAppointments(apptRes.data);
      setRepairs(repRes.data);
      setSubscriptions(subRes.data);
      setInvoices(invRes.data);
      setMessages(msgRes.data.messages || []);
      setUnreadCount(msgRes.data.unread_count || 0);
      setTowingRequests(towingRes.data);
      setMyQuotes(quotesRes.data);
      setTracking(trackingRes.data);
      setPaymentsData(paymentsRes.data || { payments: [], summary: { total_paid: 0, total_pending: 0, total_failed: 0, count: 0 } });
      setAdminQuotes(adminQuotesRes.data || []);
      // Fetch alerts (unread quotes/invoices)
      fetchAlerts();
      // Set history data
      if (historyRes.data) {
        setHistory(historyRes.data);
        setQuoteRequests(historyRes.data.quote_requests || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read_at: new Date().toISOString(), status: 'read' } : m
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Accept quote
  const acceptQuote = async (quoteId) => {
    if (!window.confirm('Accepter ce devis ?')) return;
    try {
      await axios.post(`${API}/user/quotes/${quoteId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error accepting quote:', error);
    }
  };

  // Reject quote
  const rejectQuote = async (quoteId) => {
    const reason = window.prompt('Raison du refus (optionnel):');
    try {
      await axios.post(`${API}/user/quotes/${quoteId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error rejecting quote:', error);
    }
  };

  // Pay an admin quote (devis professionnel) via SystemPay redirection
  const payAdminQuote = async (quoteId) => {
    if (!window.confirm('Etes-vous sur de vouloir payer ce devis en ligne ?')) return;
    try {
      const res = await axios.post(
        `${API}/admin-quotes/${quoteId}/pay`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.redirect_form) {
        const { redirectToSystemPay } = await import('../lib/systempayRedirect');
        redirectToSystemPay(res.data.redirect_form);
      } else {
        alert('Erreur : pas de formulaire de redirection recu.');
      }
    } catch (error) {
      console.error('Error paying admin quote:', error);
      alert(error.response?.data?.detail || 'Erreur lors du paiement du devis');
    }
  };

  // Print quote PDF
  const printQuote = (quote) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Devis ${quote.quote_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
          .company-info img { max-width: 120px; margin-bottom: 10px; }
          .company-info p { font-size: 12px; color: #666; }
          .quote-title { text-align: right; }
          .quote-title h1 { color: #dc2626; font-size: 28px; }
          .quote-title p { font-size: 14px; color: #666; }
          .client-info { background: #f5f5f5; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #1a1a1a; color: white; padding: 12px; text-align: left; }
          td { padding: 12px; border-bottom: 1px solid #ddd; }
          .totals { margin-left: auto; width: 300px; }
          .totals td { border: none; }
          .totals .total { font-size: 18px; font-weight: bold; background: #dc2626; color: white; }
          .validity { background: #fef3c7; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; font-size: 11px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <img src="https://customer-assets.emergentagent.com/job_5fa86a42-f7fc-4c7b-82aa-e23c2c93b925/artifacts/cpgdckfz_image.png" alt="Logo"/>
            <p><strong>SAS KD AUTO SERVICE</strong><br>142 boulevard Murat, 75016 Paris<br>Tél: 0805 032 032<br>SIRET: 989 707 260 00018</p>
          </div>
          <div class="quote-title">
            <h1>DEVIS</h1>
            <p><strong>${quote.quote_number}</strong></p>
            <p>Date: ${new Date(quote.created_at).toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
        
        <div class="client-info">
          <p><strong>${quote.company || ''} ${quote.firstname || ''} ${quote.name}</strong></p>
          ${quote.email ? `<p>Email: ${quote.email}</p>` : ''}
          ${quote.phone ? `<p>Tél: ${quote.phone}</p>` : ''}
          ${quote.vehicle_plate ? `<p>Véhicule: ${quote.vehicle_plate} ${quote.vehicle_model || ''}</p>` : ''}
        </div>

        <table>
          <thead>
            <tr><th>Description</th><th>Qté</th><th>P.U. HT</th><th>Total HT</th></tr>
          </thead>
          <tbody>
            ${quote.items.map(item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${((item.unit_price || item.total / item.quantity) / 1.2).toFixed(2)} €</td>
                <td>${(item.total / 1.2).toFixed(2)} €</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <table>
            <tr><td>Total HT</td><td>${(quote.total / 1.2).toFixed(2)} €</td></tr>
            <tr><td>TVA 20%</td><td>${(quote.total - quote.total / 1.2).toFixed(2)} €</td></tr>
            <tr class="total"><td>TOTAL TTC</td><td>${quote.total.toFixed(2)} €</td></tr>
          </table>
        </div>

        <div class="validity">
          <strong>⚠️ Ce devis est valable jusqu'au ${quote.validity_date ? new Date(quote.validity_date).toLocaleDateString('fr-FR') : 'N/A'}</strong>
        </div>

        ${quote.notes ? `<p style="margin: 20px 0; color: #666;"><strong>Notes:</strong> ${quote.notes}</p>` : ''}

        <div class="footer">
          <p>SAS KD AUTO SERVICE - 142 boulevard Murat, 75016 Paris - SIRET: 989 707 260 00018 - TVA: FR01989707260</p>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
              <User className="text-red-500" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{user.name}</h1>
              <p className="text-zinc-400">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 flex items-center gap-3">
                <Star className="text-yellow-500" size={24} />
                <div>
                  <p className="text-2xl font-bold text-white">{user.loyalty_points}</p>
                  <p className="text-zinc-500 text-sm">Points fidélité</p>
                </div>
              </CardContent>
            </Card>
            {user.role === 'admin' && (
              <Button onClick={() => navigate('/admin')} className="bg-red-600 hover:bg-red-700">
                Administration
              </Button>
            )}
          </div>
        </div>

        {/* Alertes : nouveaux devis / factures non lus */}
        {(alerts.quotes?.length > 0 || alerts.invoices?.length > 0) && (
          <div className="mb-6 space-y-3" data-testid="account-alerts">
            {alerts.quotes?.map((q) => (
              <div
                key={`alert-q-${q.id}`}
                className="bg-gradient-to-r from-red-600/20 to-red-900/20 border border-red-600/50 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                data-testid={`alert-quote-${q.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-600/30 flex items-center justify-center shrink-0">
                    <FileText className="text-red-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">
                      Un nouveau devis vous attend
                    </p>
                    <p className="text-zinc-300 text-sm">
                      Devis N° <strong>{q.quote_number}</strong> &middot; {Number(q.total || 0).toFixed(2)} EUR TTC
                    </p>
                    <p className="text-zinc-500 text-xs">Cliquez ci-dessous pour obtenir le devis</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => openDocumentPdf('quote', q.id)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid={`alert-quote-open-${q.id}`}
                  >
                    <Download size={16} className="mr-2" />
                    Obtenir le devis
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setActiveTab('quotes'); dismissAlert('quote', q.id); }}
                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                    data-testid={`alert-quote-view-${q.id}`}
                  >
                    Voir mes devis
                  </Button>
                </div>
              </div>
            ))}
            {alerts.invoices?.map((inv) => (
              <div
                key={`alert-i-${inv.id}`}
                className="bg-gradient-to-r from-amber-600/20 to-orange-900/20 border border-amber-500/50 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                data-testid={`alert-invoice-${inv.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center shrink-0">
                    <Receipt className="text-amber-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">
                      Une nouvelle facture vous attend
                    </p>
                    <p className="text-zinc-300 text-sm">
                      Facture N° <strong>{inv.invoice_number}</strong> &middot; {Number(inv.total || 0).toFixed(2)} EUR TTC
                    </p>
                    <p className="text-zinc-500 text-xs">Cliquez ci-dessous pour obtenir la facture</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => openDocumentPdf('invoice', inv.id)}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    data-testid={`alert-invoice-open-${inv.id}`}
                  >
                    <Download size={16} className="mr-2" />
                    Obtenir la facture
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setActiveTab('invoices'); dismissAlert('invoice', inv.id); }}
                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                    data-testid={`alert-invoice-view-${inv.id}`}
                  >
                    Voir mes factures
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-nowrap overflow-x-auto w-full justify-start">
            <TabsTrigger value="history" className="data-[state=active]:bg-red-600">
              <History className="mr-2" size={16} />
              Historique
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-red-600">
              <Calendar className="mr-2" size={16} />
              Mes RDV
            </TabsTrigger>
            <TabsTrigger value="repairs" className="data-[state=active]:bg-red-600">
              <Car className="mr-2" size={16} />
              Réparations
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-red-600 relative">
              <ClipboardList className="mr-2" size={16} />
              Mes Demandes
              {quoteRequests.filter(q => q.status === 'pending').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {quoteRequests.filter(q => q.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-red-600 relative">
              <FileText className="mr-2" size={16} />
              Mes Devis
              {myQuotes.filter(q => q.status === 'sent').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {myQuotes.filter(q => q.status === 'sent').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-red-600">
              <Receipt className="mr-2" size={16} />
              Mes Factures
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-red-600" data-testid="tab-payments">
              <CreditCard className="mr-2" size={16} />
              Mes Paiements
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-red-600 relative">
              <MessageSquare className="mr-2" size={16} />
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-red-600">
              <Gift className="mr-2" size={16} />
              Abonnements
            </TabsTrigger>
            <TabsTrigger value="towing" className="data-[state=active]:bg-red-600">
              <Truck className="mr-2" size={16} />
              Dépannage
            </TabsTrigger>
            <TabsTrigger value="tracking" className="data-[state=active]:bg-red-600 relative">
              <ListChecks className="mr-2" size={16} />
              Suivi
              {tracking.filter(t => t.status !== 'rejected' && t.status !== 'quote_sent').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {tracking.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* History Tab - NEW */}
          <TabsContent value="history">
            <div className="space-y-6">
              {/* Stats Summary */}
              {history && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4 text-center">
                      <Wallet className="mx-auto text-green-500 mb-2" size={24} />
                      <p className="text-2xl font-bold text-white">{history.stats?.total_spent?.toFixed(2) || 0}€</p>
                      <p className="text-zinc-500 text-sm">Total dépensé</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4 text-center">
                      <History className="mx-auto text-blue-500 mb-2" size={24} />
                      <p className="text-2xl font-bold text-white">{history.stats?.total_visits || 0}</p>
                      <p className="text-zinc-500 text-sm">Passages au Hub</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4 text-center">
                      <FileText className="mx-auto text-orange-500 mb-2" size={24} />
                      <p className="text-2xl font-bold text-white">{history.stats?.pending_quotes || 0}</p>
                      <p className="text-zinc-500 text-sm">Devis en attente</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4 text-center">
                      <Receipt className="mx-auto text-red-500 mb-2" size={24} />
                      <p className="text-2xl font-bold text-white">{history.stats?.unpaid_invoices || 0}</p>
                      <p className="text-zinc-500 text-sm">Factures à payer</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Visits History */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="text-red-500" size={20} />
                    Historique des passages au Hub
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-12 text-zinc-500">Chargement...</div>
                  ) : !history?.visits || history.visits.length === 0 ? (
                    <div className="text-center py-12">
                      <Car className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-500">Aucun passage enregistré</p>
                      <p className="text-zinc-600 text-sm mt-2">Vos visites au garage apparaîtront ici</p>
                      <Button onClick={() => navigate('/reservation')} className="mt-4 bg-red-600 hover:bg-red-700">
                        Prendre un RDV
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {history.visits.map((visit, idx) => (
                        <div key={visit.id || idx} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors" data-testid={`visit-${visit.id}`}>
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${
                                visit.type === 'lavage' ? 'bg-blue-500/20' :
                                visit.type === 'rdv' ? 'bg-green-500/20' :
                                'bg-purple-500/20'
                              }`}>
                                {visit.type === 'lavage' ? <Droplets className="text-blue-400" size={20} /> :
                                 visit.type === 'rdv' ? <Wrench className="text-green-400" size={20} /> :
                                 <CreditCard className="text-purple-400" size={20} />}
                              </div>
                              <div>
                                <p className="text-white font-medium">{visit.description}</p>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-400 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    {new Date(visit.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </span>
                                  {visit.vehicle_plate && (
                                    <span className="text-zinc-500">• {visit.vehicle_plate}</span>
                                  )}
                                  {visit.vehicle_model && (
                                    <span className="text-zinc-500">• {visit.vehicle_model}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {visit.payment_method && (
                                <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                                  {visit.payment_method === 'cash' ? 'Espèces' :
                                   visit.payment_method === 'card' ? 'Carte' :
                                   visit.payment_method === 'online' ? 'En ligne' :
                                   visit.payment_method === 'mixed' ? 'Mixte' :
                                   visit.payment_method}
                                </Badge>
                              )}
                              <div className="text-right">
                                <p className="text-lg font-bold text-green-500">{visit.total?.toFixed(2) || 0}€</p>
                                <Badge className={`${
                                  visit.type === 'lavage' ? 'bg-blue-500/20 text-blue-400' :
                                  visit.type === 'rdv' ? 'bg-green-500/20 text-green-400' :
                                  'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {visit.type === 'lavage' ? 'Lavage' :
                                   visit.type === 'rdv' ? 'RDV' :
                                   'Caisse'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Quote Requests Tab - NEW */}
          <TabsContent value="requests">
            <div className="space-y-4">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ClipboardList className="text-orange-500" size={20} />
                    Mes Demandes de Devis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-12 text-zinc-500">Chargement...</div>
                  ) : quoteRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <ClipboardList className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-500">Aucune demande de devis</p>
                      <p className="text-zinc-600 text-sm mt-2">Envoyez une demande de devis pour vos travaux</p>
                      <Button onClick={() => navigate('/devis')} className="mt-4 bg-red-600 hover:bg-red-700">
                        Demander un devis
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {quoteRequests.map((req) => (
                        <div key={req.id} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700" data-testid={`quote-request-${req.id}`}>
                          <div className="flex flex-col md:flex-row md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h4 className="text-white font-medium">{req.service_type}</h4>
                                <Badge className={
                                  req.status === 'pending' ? 'bg-yellow-500' :
                                  req.status === 'quoted' ? 'bg-blue-500' :
                                  req.status === 'accepted' ? 'bg-green-500' :
                                  req.status === 'rejected' ? 'bg-red-500' :
                                  'bg-gray-500'
                                }>
                                  {req.status === 'pending' ? '⏳ En attente' :
                                   req.status === 'quoted' ? '📋 Devis reçu' :
                                   req.status === 'accepted' ? '✅ Accepté' :
                                   req.status === 'rejected' ? '❌ Refusé' :
                                   req.status}
                                </Badge>
                              </div>
                              <p className="text-zinc-400 text-sm mb-2">
                                Envoyée le {new Date(req.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                              <div className="space-y-1 text-sm text-zinc-300">
                                <p>🚗 {req.vehicle_type === 'auto' ? 'Voiture' : 'Moto'} - {req.vehicle_brand} {req.vehicle_model}</p>
                                {req.vehicle_year && <p>📅 Année: {req.vehicle_year}</p>}
                              </div>
                              <p className="text-zinc-500 mt-2 text-sm italic">"{req.description}"</p>
                              {req.attachments && req.attachments.length > 0 && (
                                <p className="text-zinc-500 text-xs mt-2">📎 {req.attachments.length} pièce(s) jointe(s)</p>
                              )}
                            </div>
                            <div className="text-right">
                              {req.estimated_price ? (
                                <div>
                                  <p className="text-zinc-500 text-sm">Estimation</p>
                                  <p className="text-2xl font-bold text-green-500">{req.estimated_price}€</p>
                                </div>
                              ) : (
                                <p className="text-zinc-500 text-sm">En cours d'étude...</p>
                              )}
                              {req.admin_notes && (
                                <p className="text-zinc-400 text-sm mt-2 bg-zinc-800 p-2 rounded">
                                  💬 {req.admin_notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12 text-zinc-500">Chargement...</div>
              ) : appointments.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <Calendar className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucun rendez-vous</p>
                    <Button onClick={() => navigate('/reservation')} className="mt-4 bg-red-600 hover:bg-red-700">
                      Prendre un RDV
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                appointments.map((apt) => (
                  <Card key={apt.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`appointment-${apt.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">{apt.service_name}</h3>
                            <Badge className={`${statusColors[apt.status]} text-white`}>
                              {statusLabels[apt.status]}
                            </Badge>
                          </div>
                          <p className="text-zinc-400">
                            {apt.vehicle_brand} {apt.vehicle_model} • {apt.vehicle_plate}
                          </p>
                          {apt.notes && <p className="text-zinc-500 mt-2 text-sm">{apt.notes}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-red-500 font-bold text-lg">
                            {new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-zinc-400 flex items-center justify-end gap-1">
                            <Clock size={14} /> {apt.time_slot}
                          </p>
                        </div>
                      </div>
                      {canEditAppt(apt) && (
                        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-zinc-800">
                          <Button
                            size="sm" variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            onClick={() => openEditAppt(apt)}
                            data-testid={`edit-appt-${apt.id}`}
                          >
                            <Edit2 size={14} className="mr-1" /> Modifier
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="border-red-700 text-red-400 hover:bg-red-900/30"
                            onClick={() => cancelAppt(apt)}
                            data-testid={`cancel-appt-${apt.id}`}
                          >
                            <Trash2 size={14} className="mr-1" /> Annuler
                          </Button>
                        </div>
                      )}
                      {!canEditAppt(apt) && apt.status !== 'cancelled' && apt.status !== 'completed' && (
                        <p className="text-xs text-zinc-500 mt-3 italic">
                          Modification impossible : RDV dans moins de 3 heures
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Repairs Tab */}
          <TabsContent value="repairs">
            <div className="space-y-4">
              {repairs.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <Car className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucune réparation en cours</p>
                    <Button onClick={() => navigate('/suivi')} className="mt-4 bg-red-600 hover:bg-red-700">
                      Suivre une réparation
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                repairs.map((repair) => (
                  <Card key={repair.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-white">
                            {repair.vehicle_brand} {repair.vehicle_model}
                          </h3>
                          <p className="text-zinc-400">{repair.service_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-red-500">{repair.progress}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-zinc-800 rounded-full h-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full transition-all"
                          style={{ width: `${repair.progress}%` }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* My Quotes Tab */}
          <TabsContent value="quotes">
            <div className="space-y-4">
              {/* Admin quotes (devis pros payables en ligne) */}
              {adminQuotes && adminQuotes.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="admin-quotes-card">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="text-red-500" size={20} />
                      Devis a payer
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {adminQuotes.map((q) => (
                        <div
                          key={q.id}
                          className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg flex flex-col md:flex-row gap-3 md:items-center justify-between"
                          data-testid={`admin-quote-${q.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-mono font-bold text-red-400">{q.quote_number}</span>
                              <Badge className={
                                q.status === 'paid' ? 'bg-green-500' :
                                q.status === 'sent' ? 'bg-blue-500' :
                                q.status === 'accepted' ? 'bg-orange-500' :
                                q.status === 'converted' ? 'bg-purple-500' : 'bg-gray-500'
                              }>
                                {q.status === 'paid' ? 'Paye' :
                                 q.status === 'sent' ? 'A payer' :
                                 q.status === 'accepted' ? 'Accepte' :
                                 q.status === 'converted' ? 'Facture' : q.status}
                              </Badge>
                            </div>
                            <p className="text-zinc-400 text-sm truncate">
                              {q.items?.[0]?.description || 'Prestation'}{q.items?.length > 1 ? ` (+${q.items.length - 1} autre(s))` : ''}
                            </p>
                            {q.validity_date && q.status === 'sent' && (
                              <p className="text-orange-400 text-xs mt-1">
                                Valide jusqu'au {new Date(q.validity_date).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xl font-bold text-white tabular-nums">{(q.total || 0).toFixed(2)}€</span>
                            {(q.status === 'sent' || q.status === 'accepted') ? (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => payAdminQuote(q.id)}
                                data-testid={`pay-quote-btn-${q.id}`}
                              >
                                <CreditCard className="mr-1" size={14} /> Payer
                              </Button>
                            ) : q.status === 'paid' ? (
                              <Badge className="bg-green-700">Paye</Badge>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {myQuotes.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <FileText className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucun devis</p>
                    <p className="text-zinc-600 text-sm mt-2">Vos devis apparaîtront ici une fois établis</p>
                  </CardContent>
                </Card>
              ) : (
                myQuotes.map((quote) => (
                  <Card key={quote.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className="text-red-500 font-mono font-bold text-lg">{quote.quote_number}</span>
                            <Badge className={
                              quote.status === 'sent' ? 'bg-blue-500' :
                              quote.status === 'accepted' ? 'bg-green-500' :
                              quote.status === 'rejected' ? 'bg-red-500' :
                              quote.status === 'converted' ? 'bg-purple-500' :
                              'bg-gray-500'
                            }>
                              {quote.status === 'sent' ? '📩 En attente de réponse' :
                               quote.status === 'accepted' ? '✅ Accepté' :
                               quote.status === 'rejected' ? '❌ Refusé' :
                               quote.status === 'converted' ? '📄 Converti en facture' :
                               quote.status}
                            </Badge>
                          </div>
                          <p className="text-zinc-400 text-sm mb-2">
                            Reçu le {new Date(quote.sent_at || quote.created_at).toLocaleDateString('fr-FR')}
                            {quote.validity_date && (
                              <span className="text-orange-400 ml-2">
                                • Valide jusqu'au {new Date(quote.validity_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </p>
                          {quote.vehicle_plate && (
                            <p className="text-zinc-300">🚗 {quote.vehicle_plate} {quote.vehicle_model || ''}</p>
                          )}
                          <div className="mt-3 pt-3 border-t border-zinc-800">
                            <p className="text-zinc-400 text-sm mb-1">Prestations :</p>
                            <ul className="text-zinc-300 text-sm space-y-1">
                              {quote.items?.slice(0, 3).map((item, idx) => (
                                <li key={idx}>• {item.description} - {item.total?.toFixed(2)} €</li>
                              ))}
                              {quote.items?.length > 3 && <li className="text-zinc-500">+ {quote.items.length - 3} autre(s)...</li>}
                            </ul>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          <div className="text-right">
                            <p className="text-zinc-500 text-sm">Total TTC</p>
                            <p className="text-3xl font-bold text-white">{quote.total?.toFixed(2)} €</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-zinc-600"
                              onClick={() => printQuote(quote)}
                            >
                              <Download size={14} className="mr-1" /> Télécharger
                            </Button>
                            {quote.status === 'sent' && (
                              <>
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => acceptQuote(quote.id)}
                                >
                                  <Check size={14} className="mr-1" /> Accepter
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => rejectQuote(quote.id)}
                                >
                                  <X size={14} className="mr-1" /> Refuser
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="space-y-4">
              {invoices.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <Receipt className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucune facture</p>
                    <p className="text-zinc-600 text-sm mt-2">Vos factures apparaîtront ici après vos prestations</p>
                  </CardContent>
                </Card>
              ) : (
                invoices.map((invoice) => (
                  <Card key={invoice.id} className={`bg-zinc-900/50 border-zinc-800 ${invoice.status === 'paid' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-yellow-500'}`} data-testid={`invoice-${invoice.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">{invoice.invoice_number}</h3>
                            <Badge className={`${statusColors[invoice.status]} text-white`}>
                              {statusLabels[invoice.status]}
                            </Badge>
                          </div>
                          <p className="text-zinc-500 text-sm mb-3">
                            Émise le {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                            {invoice.due_date && ` • Échéance: ${new Date(invoice.due_date).toLocaleDateString('fr-FR')}`}
                          </p>
                          
                          {/* Items */}
                          <div className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                            {invoice.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm mb-1">
                                <span className="text-zinc-300">{item.description} x{item.quantity}</span>
                                <span className="text-zinc-400">{item.total.toFixed(2)} €</span>
                              </div>
                            ))}
                            <div className="border-t border-zinc-700 mt-2 pt-2">
                              <div className="flex justify-between text-sm text-zinc-400">
                                <span>Sous-total HT</span>
                                <span>{invoice.subtotal.toFixed(2)} €</span>
                              </div>
                              <div className="flex justify-between text-sm text-zinc-400">
                                <span>TVA ({invoice.tax_rate}%)</span>
                                <span>{invoice.tax_amount.toFixed(2)} €</span>
                              </div>
                              <div className="flex justify-between font-bold text-white mt-1">
                                <span>Total TTC</span>
                                <span className="text-green-500">{invoice.total.toFixed(2)} €</span>
                              </div>
                            </div>
                          </div>
                          
                          {invoice.notes && (
                            <p className="text-zinc-500 text-sm italic">{invoice.notes}</p>
                          )}
                        </div>
                        <div className="ml-4 text-right flex flex-col items-end gap-3">
                          <p className="text-2xl font-bold text-green-500">{invoice.total.toFixed(2)} €</p>
                          {invoice.status === 'paid' && invoice.paid_date && (
                            <p className="text-green-500 text-sm">
                              <CheckCircle className="inline mr-1" size={14} />
                              Payée le {new Date(invoice.paid_date).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                          {invoice.status === 'sent' && (
                            <p className="text-yellow-500 text-sm">
                              <AlertCircle className="inline mr-1" size={14} />
                              En attente de paiement
                            </p>
                          )}
                          {/* Download Button */}
                          <Button 
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 mt-2"
                            onClick={() => downloadInvoice(invoice)}
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download size={14} className="mr-2" />
                            Télécharger PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Payments Tab — vue unifiee de toutes les transactions */}
          <TabsContent value="payments">
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card className="bg-green-500/10 border-green-500/30" data-testid="payments-summary-paid">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="text-green-400" size={16} />
                      <span className="text-zinc-400 text-xs">Paye</span>
                    </div>
                    <p className="text-2xl font-bold text-green-400 mt-1">{(paymentsData.summary?.total_paid || 0).toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-500/10 border-orange-500/30" data-testid="payments-summary-pending">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="text-orange-400" size={16} />
                      <span className="text-zinc-400 text-xs">En attente</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-400 mt-1">{(paymentsData.summary?.total_pending || 0).toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30" data-testid="payments-summary-failed">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="text-red-400" size={16} />
                      <span className="text-zinc-400 text-xs">Echec</span>
                    </div>
                    <p className="text-2xl font-bold text-red-400 mt-1">{(paymentsData.summary?.total_failed || 0).toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/50 border-zinc-700" data-testid="payments-summary-count">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="text-zinc-400" size={16} />
                      <span className="text-zinc-400 text-xs">Total transactions</span>
                    </div>
                    <p className="text-2xl font-bold text-white mt-1">{paymentsData.summary?.count || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Transactions list */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="text-red-500" size={20} />
                    Historique des paiements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(paymentsData.payments || []).length === 0 ? (
                    <p className="text-zinc-500 text-center py-8" data-testid="payments-empty">
                      Aucune transaction pour le moment.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {paymentsData.payments.map((p, idx) => {
                        const statusLabel = {
                          paid: 'Paye',
                          pending: 'En attente',
                          pending_payment: 'En attente paiement',
                          pending_onsite: 'A regler sur place',
                          confirmed: 'Confirme',
                          failed: 'Echec',
                          payment_failed: 'Echec paiement',
                          cancelled: 'Annule',
                          refunded: 'Rembourse',
                          active: 'Actif',
                        }[p.payment_status] || p.payment_status;
                        const statusColor = {
                          paid: 'bg-green-500',
                          confirmed: 'bg-green-500',
                          active: 'bg-green-500',
                          pending: 'bg-orange-500',
                          pending_payment: 'bg-orange-500',
                          pending_onsite: 'bg-blue-500',
                          failed: 'bg-red-500',
                          payment_failed: 'bg-red-500',
                          cancelled: 'bg-zinc-500',
                          refunded: 'bg-purple-500',
                        }[p.payment_status] || 'bg-zinc-500';
                        const typeIcon = {
                          shop: Receipt,
                          wash: Droplets,
                          towing: Truck,
                          subscription: Gift,
                        }[p.type] || CreditCard;
                        const TypeIcon = typeIcon;
                        return (
                          <div
                            key={`${p.type}-${p.reference}-${idx}`}
                            className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-zinc-600 transition"
                            data-testid={`payment-row-${idx}`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-zinc-700/50 flex items-center justify-center shrink-0">
                                <TypeIcon size={18} className="text-zinc-300" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-white font-medium">{p.type_label}</span>
                                  <span className="text-zinc-500 text-xs">•</span>
                                  <span className="text-zinc-400 text-xs font-mono">{p.reference}</span>
                                </div>
                                <p className="text-zinc-500 text-xs truncate">{p.details}</p>
                                {p.created_at && (
                                  <p className="text-zinc-600 text-xs">
                                    {new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <Badge className={`${statusColor} text-white`}>{statusLabel}</Badge>
                              <span className="text-lg font-bold text-white tabular-nums">{p.amount.toFixed(2)}€</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <p className="text-zinc-500 text-xs text-center">
                Pour telecharger une facture en PDF, rendez-vous dans l'onglet "Mes Factures".
              </p>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="text-blue-400" size={20} />
                  Mes Messages
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 ml-2">{unreadCount} non lu(s)</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucun message</p>
                    <p className="text-zinc-600 text-sm mt-2">
                      Les messages du garage apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-4 rounded-lg border ${!msg.read_at ? 'bg-blue-900/20 border-blue-700' : 'bg-zinc-800/30 border-zinc-700'}`}
                        onClick={() => !msg.read_at && markAsRead(msg.id)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-white font-medium">{msg.subject}</h4>
                            <p className="text-zinc-500 text-xs">
                              De: {msg.sender_name} • {new Date(msg.created_at).toLocaleDateString('fr-FR', { 
                                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {!msg.read_at && (
                            <Badge className="bg-blue-500">Nouveau</Badge>
                          )}
                        </div>
                        <p className="text-zinc-300 whitespace-pre-wrap">{msg.message}</p>
                        {msg.read_at && (
                          <p className="text-zinc-600 text-xs mt-2">
                            Lu le {new Date(msg.read_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-4">
              {subscriptions.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <Gift className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucun abonnement actif</p>
                    <Button onClick={() => navigate('/tarifs-lavage')} className="mt-4 bg-red-600 hover:bg-red-700">
                      Découvrir nos formules
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                subscriptions.map((sub) => (
                  <Card key={sub.id} className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-green-500">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-white">{sub.plan_name}</h3>
                          <p className="text-zinc-400">
                            {sub.washes_remaining} lavages restants
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-500 text-white">Actif</Badge>
                          <p className="text-zinc-500 text-sm mt-1">
                            Expire le {new Date(sub.end_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Towing Requests Tab */}
          <TabsContent value="towing">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12 text-zinc-500">Chargement...</div>
              ) : towingRequests.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 text-center">
                  <CardContent className="py-12">
                    <Truck className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucune demande de dépannage</p>
                    <Button onClick={() => navigate('/depannage')} className="mt-4 bg-red-600 hover:bg-red-700">
                      Demander un dépannage
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                towingRequests.map((req) => (
                  <Card key={req.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`towing-${req.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white capitalize">
                              {req.intervention_type === 'remorquage' ? 'Remorquage' : 'Dépannage sur place'}
                            </h3>
                            <Badge className={`${statusColors[req.status] || 'bg-zinc-500'} text-white`}>
                              {statusLabels[req.status] || req.status}
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <p className="text-zinc-300">
                              <Car className="inline mr-2" size={14} />
                              {req.vehicle_brand} {req.vehicle_model} • {req.vehicle_plate}
                            </p>
                            <p className="text-zinc-400">
                              <MapPin className="inline mr-2" size={14} />
                              {req.location}
                            </p>
                            {req.destination && (
                              <p className="text-zinc-400">
                                → {req.destination}
                              </p>
                            )}
                            {req.description && (
                              <p className="text-zinc-500 mt-2 italic">"{req.description}"</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right space-y-2">
                          <p className="text-red-500 font-bold text-lg">
                            {new Date(req.preferred_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-zinc-400 flex items-center justify-end gap-1">
                            <Clock size={14} /> {req.preferred_time}
                          </p>
                          <div className="pt-2 border-t border-zinc-800">
                            <p className="text-zinc-400 text-sm">Acompte</p>
                            <p className={`font-bold ${req.deposit_paid ? 'text-green-500' : 'text-orange-500'}`}>
                              {req.deposit_amount}€ {req.deposit_paid ? '(Payé)' : '(En attente)'}
                            </p>
                          </div>
                          {req.final_price && (
                            <div>
                              <p className="text-zinc-400 text-sm">Prix final</p>
                              <p className="text-white font-bold">{req.final_price}€</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tracking Tab */}
          <TabsContent value="tracking">
            <div className="space-y-4">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ListChecks className="text-blue-500" size={20} />
                    Suivi de mes demandes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tracking.length === 0 ? (
                    <div className="text-center py-12">
                      <ListChecks className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-500">Aucune demande en cours</p>
                      <p className="text-zinc-600 text-sm mt-2">Faites une demande de devis pour suivre son avancement ici</p>
                      <Button onClick={() => navigate('/devis')} className="mt-4 bg-red-600 hover:bg-red-700">
                        Demander un devis
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {tracking.map((item) => (
                        <div key={item.id} className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden" data-testid={`tracking-${item.id}`}>
                          {/* Header */}
                          <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-bold text-lg">{item.service_type}</h4>
                              <p className="text-zinc-400 text-sm">{item.vehicle} - {new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                            <Badge className={
                              item.status === 'pending' ? 'bg-yellow-600' :
                              item.status === 'in_progress' ? 'bg-blue-600' :
                              item.status === 'quote_sent' ? 'bg-purple-600' :
                              item.status === 'rejected' ? 'bg-red-600' :
                              'bg-zinc-600'
                            }>
                              {item.status === 'pending' ? 'En attente' :
                               item.status === 'in_progress' ? 'En cours' :
                               item.status === 'quote_sent' ? 'Devis envoyé' :
                               item.status === 'rejected' ? 'Refusé' :
                               item.status}
                            </Badge>
                          </div>

                          {/* Progress Steps */}
                          <div className="p-4">
                            <div className="flex items-center gap-0 mb-4">
                              {/* Step 1: Demande */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${true ? 'bg-green-600 text-white' : 'bg-zinc-700 text-zinc-400'}`}>
                                  <Check size={18} />
                                </div>
                                <p className="text-xs text-zinc-400 mt-1 text-center">Demande</p>
                              </div>
                              <div className={`flex-1 h-1 ${item.status !== 'pending' ? 'bg-green-600' : 'bg-zinc-700'}`} />
                              
                              {/* Step 2: Devis */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${item.linked_quote ? 'bg-green-600 text-white' : item.status === 'in_progress' ? 'bg-blue-600 text-white animate-pulse' : 'bg-zinc-700 text-zinc-400'}`}>
                                  {item.linked_quote ? <Check size={18} /> : '2'}
                                </div>
                                <p className="text-xs text-zinc-400 mt-1 text-center">Devis</p>
                              </div>
                              <div className={`flex-1 h-1 ${item.linked_quote && (item.linked_quote.status === 'accepted' || item.linked_quote.status === 'converted') ? 'bg-green-600' : 'bg-zinc-700'}`} />
                              
                              {/* Step 3: Validation */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${item.linked_quote && item.linked_quote.status === 'accepted' ? 'bg-green-600 text-white' : item.linked_quote && item.linked_quote.status === 'sent' ? 'bg-orange-500 text-white animate-pulse' : 'bg-zinc-700 text-zinc-400'}`}>
                                  {item.linked_quote && item.linked_quote.status === 'accepted' ? <Check size={18} /> : '3'}
                                </div>
                                <p className="text-xs text-zinc-400 mt-1 text-center">Validation</p>
                              </div>
                              <div className={`flex-1 h-1 ${item.linked_invoice ? 'bg-green-600' : 'bg-zinc-700'}`} />
                              
                              {/* Step 4: Facture */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${item.linked_invoice && item.linked_invoice.status === 'paid' ? 'bg-green-600 text-white' : item.linked_invoice ? 'bg-orange-500 text-white' : 'bg-zinc-700 text-zinc-400'}`}>
                                  {item.linked_invoice && item.linked_invoice.status === 'paid' ? <Check size={18} /> : '4'}
                                </div>
                                <p className="text-xs text-zinc-400 mt-1 text-center">Facture</p>
                              </div>
                            </div>

                            {/* Details */}
                            <div className="space-y-2 text-sm">
                              {item.description && (
                                <p className="text-zinc-500 italic">"{item.description}"</p>
                              )}
                              
                              {item.estimated_price && (
                                <p className="text-zinc-300">Estimation : <span className="text-green-400 font-bold">{item.estimated_price}€</span></p>
                              )}
                              
                              {item.admin_notes && (
                                <div className="bg-zinc-900 p-3 rounded-lg">
                                  <p className="text-zinc-400"><MessageSquare className="inline mr-1" size={14} /> Note du garage : {item.admin_notes}</p>
                                </div>
                              )}

                              {item.linked_quote && (
                                <div className="bg-zinc-900 p-3 rounded-lg flex items-center justify-between">
                                  <div>
                                    <p className="text-zinc-300"><FileText className="inline mr-1" size={14} /> Devis {item.linked_quote.quote_number}</p>
                                    <p className="text-zinc-500 text-xs">
                                      {item.linked_quote.status === 'sent' ? 'En attente de votre réponse' :
                                       item.linked_quote.status === 'accepted' ? 'Accepté' :
                                       item.linked_quote.status === 'converted' ? 'Converti en facture' :
                                       item.linked_quote.status}
                                    </p>
                                  </div>
                                  <p className="text-white font-bold text-lg">{item.linked_quote.total?.toFixed(2)}€</p>
                                </div>
                              )}

                              {item.linked_invoice && (
                                <div className={`p-3 rounded-lg flex items-center justify-between ${item.linked_invoice.status === 'paid' ? 'bg-green-900/30 border border-green-800' : 'bg-zinc-900'}`}>
                                  <div>
                                    <p className="text-zinc-300"><Receipt className="inline mr-1" size={14} /> Facture {item.linked_invoice.invoice_number}</p>
                                    <p className={`text-xs ${item.linked_invoice.status === 'paid' ? 'text-green-400' : 'text-zinc-500'}`}>
                                      {item.linked_invoice.status === 'paid' ? `Payée${item.linked_invoice.paid_date ? ` le ${new Date(item.linked_invoice.paid_date).toLocaleDateString('fr-FR')}` : ''}` : 'En attente de paiement'}
                                    </p>
                                  </div>
                                  <p className="text-white font-bold text-lg">{item.linked_invoice.total?.toFixed(2)}€</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog modification RDV mecanique */}
      <Dialog open={!!editingAppt} onOpenChange={(open) => !open && setEditingAppt(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="edit-appt-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Modifier mon rendez-vous</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400">Date</Label>
              <Input
                type="date" value={apptForm.date || ''}
                onChange={(e) => setApptForm({ ...apptForm, date: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="edit-appt-date"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Heure</Label>
              <Input
                type="time" value={apptForm.time_slot || ''}
                onChange={(e) => setApptForm({ ...apptForm, time_slot: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="edit-appt-time"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-400">Marque</Label>
                <Input
                  value={apptForm.vehicle_brand || ''}
                  onChange={(e) => setApptForm({ ...apptForm, vehicle_brand: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Modele</Label>
                <Input
                  value={apptForm.vehicle_model || ''}
                  onChange={(e) => setApptForm({ ...apptForm, vehicle_model: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Immatriculation</Label>
              <Input
                value={apptForm.vehicle_plate || ''}
                onChange={(e) => setApptForm({ ...apptForm, vehicle_plate: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Notes</Label>
              <Textarea
                value={apptForm.notes || ''}
                onChange={(e) => setApptForm({ ...apptForm, notes: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                rows={3}
              />
            </div>
            <p className="text-xs text-zinc-500 italic">
              Modification possible jusqu'a 3 heures avant le RDV.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAppt(null)} className="border-zinc-700">Annuler</Button>
            <Button onClick={saveAppt} disabled={apptSaving} className="bg-red-600 hover:bg-red-700" data-testid="save-appt-btn">
              {apptSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
